import {
  type CompanySalesTermsDTO,
  type CreatCustomerDTO,
  type Currency,
  type CustomerDTO,
  type DbLogFields,
  type DiscountDTO,
  type PickupDTO,
  type ProductLightDTO,
  type ServiceDTO
} from './grand-connect'
import { type CartItem } from './cart'
import type { CountryCode } from './countries'
import type { IngridCreateSessionResponse } from './ingrid'
import type { Option } from './general'
import type {
  NexiCreatePaymentResponse,
  NexiRetrievePaymentResponse,
  NexiUpdateOrderRequest
} from './nexi'
import { type SveaOrder, type SveaUpdateOrderModel } from './svea'
import { type QliroOrder, type QliroUpdateOrderRequest } from './qliro'
import { type BriqpaySession, type BriqpayUpdateSessionRequest } from './briqpay'
import { type KlarnaOrder, type KlarnaUpdateOrderRequest } from './klarna'
import { type PostNordAddress, type PostNordCheckoutDeliveryOptionSelected } from './postnord'
import type { PaymentProviderName, ShippingProviderName } from './storefront'
import type { ShippingMethodKind } from './shipping'
import {
  type NshiftCheckoutWidgetResult,
  type NshiftCreateSessionResponse,
  type NshiftCreateShipmentResponse,
  type NshiftReceiver
} from './nshift'

export enum OrderStatus {
  CANCELLED = 'CANCELLED',
  CREATED = 'CREATED',
  DELIVERED = 'DELIVERED',
  DRAFT = 'DRAFT',
  PAID = 'PAID',
  PARTIALLY_DELIVERED = 'PARTIALLY_DELIVERED',
  PROCESSING = 'PROCESSING',
  READY = 'READY',
  PARTIALLY_RETURNED = 'PARTIALLY_RETURNED',
  RETURNED = 'RETURNED',
  SETTLED = 'SETTLED'
}

export enum PaymentType {
  /** Card / Klarna / Svea / Nexi / … — paid at checkout (B2C / upfront path). */
  UPFRONT = 'UPFRONT',
  /** Invoice-billed B2B — shipped on terms, the company is invoiced afterwards. */
  INVOICE = 'INVOICE',
  /** Invoice-billed B2B but prepaid — a pro-forma document is issued and payment
   *  is required before fulfilment. SF treats it identically to INVOICE (same
   *  `/storefront/invoice` flow); the prepayment gate is enforced downstream by
   *  OM. Resolved server-side from the company's sales terms. */
  PROFORMA = 'PROFORMA'
}

/**
 * True for invoice-billed orders — `INVOICE` or `PROFORMA`. Both go through the
 * `/storefront/invoice` flow and never carry payment-provider fields; the
 * distinction between them is display + a downstream prepayment gate, not flow.
 * Use this instead of `=== PaymentType.INVOICE` at every branch point so a
 * PROFORMA order isn't mistaken for a card order.
 */
export const isInvoiceBilled = (paymentType?: Option<PaymentType>): boolean =>
  paymentType === PaymentType.INVOICE || paymentType === PaymentType.PROFORMA

/**
 * Predict the invoice-billed variant a company resolves to, before any DRAFT is
 * created — mirrors the BE's server-side resolution
 * (FEATURE_B2B_ORDER_PLACEMENT §5.1 step 4): `PROFORMA` when the company's sales
 * terms say so, `INVOICE` otherwise (no profile / no sales terms / `UPFRONT` all
 * fall back to `INVOICE`, since the invoice endpoint is invoice-billed by
 * definition). The BE stays the source of truth — once a DRAFT exists, read the
 * resolved `Order.paymentType` instead of predicting.
 */
export const resolveInvoicePaymentType = (
  companySalesTerms?: Option<CompanySalesTermsDTO>
): PaymentType.INVOICE | PaymentType.PROFORMA =>
  companySalesTerms?.paymentType === 'PROFORMA' ? PaymentType.PROFORMA : PaymentType.INVOICE

/**
 * Whether a company's sales terms route its storefront checkout to the
 * invoice-billed flow (`/storefront/invoice`). True **only** when the terms
 * explicitly specify an invoice-billed type (`INVOICE` or `PROFORMA`) — no sales
 * terms, no `paymentType`, or an explicit `UPFRONT` all mean the company checks
 * out through the regular upfront (card) flow. Drives the storefront's choice of
 * order flow now that it is derived from the company profile rather than picked
 * by the shopper. Once the invoice flow is entered, {@link resolveInvoicePaymentType}
 * decides INVOICE vs PROFORMA.
 */
export const companyUsesInvoiceFlow = (companySalesTerms?: Option<CompanySalesTermsDTO>): boolean =>
  companySalesTerms?.paymentType === 'INVOICE' || companySalesTerms?.paymentType === 'PROFORMA'

/** Data could be extended or changed without changing the JAVA BE */
export interface OrderShippingAdditionalDataDTO {
  postnordDetails?: {
    isPickupFromStore?: boolean
    selectedPickup?: PickupDTO
    recipientAddress?: PostNordAddress
    deliveryOption?: PostNordCheckoutDeliveryOptionSelected
    bookingDetails?: {
      bookingId: string
      trackingUrl?: string
      trackingNumber?: string
      labelUrl?: string
    }
  }
  nshiftDetails?: {
    isPickupFromStore?: boolean
    widgetResult?: NshiftCheckoutWidgetResult
    recipientAddress?: NshiftReceiver
    bookingDetails?: {
      bookingOrderId: NshiftCreateShipmentResponse['orderId']
      pickupPoint?: NshiftCreateShipmentResponse['pickupPoint']
      carrier: NshiftCreateShipmentResponse['carrier']
      carrierProduct: NshiftCreateShipmentResponse['carrierProduct']
    }
  }
  /**
   * FE-driven delivery selection used when the store has no third-party
   * carrier configured (DEFAULT shipping branch). Mirrors `postnordDetails`
   * in spirit: stores the raw shopper choice (delivery vs pickup, the
   * picked pickup location, the picked shipping address) so the BE can
   * reconstruct a canonical `ShipmentDTO` without re-doing the UI choice.
   */
  defaultDetails?: {
    isPickupFromStore?: boolean
    selectedPickup?: PickupDTO
    shippingAddress?: OrderAddressDTO
    /** The delivery option the shopper picked, when the store configures more
     *  than the implicit one. Mirrored onto `ShipmentDTO.carrierService` /
     *  `carrierName` so fulfilment sees the chosen service without reading
     *  this blob. */
    selectedMethod?: {
      id: string
      label: string
      kind: ShippingMethodKind
      /** Price in major units, as charged to this buyer. */
      price: number
    }
  }
}

export type OrderShippingType = 'DELIVERY' | 'PICKUP'

export interface OrderShippingDTO {
  /**
   * If user chose pickup from store we set PICKUP, otherwise it's DELIVERY
   */
  shippingType?: OrderShippingType

  /** Human-readable name of the carrier, e.g. "DHL", "PostNord" */
  carrierName?: string

  /** Service level like "Standard", "Express", "MyPack", etc. */
  carrierService?: string

  /** Unique code used for tracking the shipment */
  trackingNumber?: string

  /** Full URL to track the shipment online */
  trackingUrl?: string

  /** Optional link to the shipping label in PDF format (if available) */
  labelUrl?: string

  /** ID of the parcel locker or service point (if used) */
  pickupLocationId?: string

  /** Name of the service point */
  pickupLocationName?: string

  /** Address of the pickup location */
  pickupAddress?: OrderAddressDTO

  /** Promised delivery date (ISO 8601 string) */
  estimatedDeliveryDate?: string

  /** When the package was shipped (ISO 8601 string) */
  shippedDate?: string

  /** Actual delivery timestamp if fulfilled (ISO 8601 string) */
  deliveryDate?: string

  /**
   * Raw pass through data from carrier if needed
   * @remarks Data should be an object with anything inside
   * @example
   * ```js
   *  something: "This is something",
   *  something2: {
   *    extra: "This can be an inside object",
   *  }
   * ```
   */
  additionalInfo?: OrderShippingAdditionalDataDTO
}

export enum ShipmentStatus {
  CREATED = 'CREATED',
  PROCESSING = 'PROCESSING',
  SHIPPED = 'SHIPPED',
  DELIVERED = 'DELIVERED',
  BLOCKED = 'BLOCKED',
  CANCELLED = 'CANCELLED',
  DELETED = 'DELETED'
}

export interface ShipmentItemDTO {
  productId: string
  quantity: number
}

export interface ShipmentDTO {
  id?: string
  shipmentId?: string
  status?: ShipmentStatus
  warehouseId?: string
  warehouseCode?: string
  shippingProvider?: ShippingProviderName
  shippingType?: OrderShippingType
  carrierName?: string
  carrierCode?: string
  carrierService?: string
  trackingNumber?: string
  trackingUrl?: string
  labelUrl?: string
  locationId?: string
  locationName?: string
  address?: OrderAddressDTO
  estimatedDeliveryDate?: string
  shippedDate?: string
  deliveryDate?: string
  items?: ShipmentItemDTO[]
  additionalInfo?: OrderShippingAdditionalDataDTO
}

/**
 * Attribution block carried by the order-history endpoints
 * (`GET /storefront/customer/{id}/orders` and `.../orders/{orderId}/items`).
 *
 * Present on own-only reads too — where `mine` is always `true` — so the
 * response shape does not change when a company flips `sharedOrderHistory`.
 * See FEATURE_ORDER_HISTORY §4.3.
 */
export interface PlacedByDTO {
  customerId: string
  /** Null when the Customer no longer resolves (deleted). The order still lists — render a placeholder. */
  name?: Option<string>
  /**
   * Populated only when `mine === true`. A shared order history is deliberately
   * not a company directory export, so do not build a "contact the buyer"
   * affordance on this.
   */
  email?: Option<string>
}

export type OrderDTO = DbLogFields & {
  id: string
  billingAddress?: OrderAddressDTO
  currency?: Currency
  /**
   * Omitted on the order-history endpoints for an order the shopper did not
   * place themselves — the BE will not hand out a colleague's email, phones or
   * addresses. Use {@link PlacedByDTO} for attribution instead of this block.
   */
  customer?: Option<CustomerDTO>
  discountCode?: string
  items?: OrderItemDTO[]
  orderAmount?: number
  orderDiscount?: number
  orderNumber?: Option<string>
  orderTaxAmount?: number
  paymentId?: string
  paymentProvider?: PaymentProviderName
  /**
   * Order-flow discriminator. UPFRONT (card-paid) vs the invoice-billed B2B
   * variants INVOICE / PROFORMA (see {@link isInvoiceBilled}). PROFORMA is
   * resolved server-side from the company's sales terms and is prepaid before
   * fulfilment; SF treats INVOICE and PROFORMA identically.
   * Legacy orders persisted before the field landed return null → treat as UPFRONT.
   * Item prices and totals are stored GROSS on both flows; `orderTaxAmount` is
   * the VAT portion already included. B2B ex-VAT display is a FE render-time
   * transformation.
   */
  paymentType?: PaymentType
  /**
   * Who placed the order. Order-history endpoints only — absent on create /
   * update and on `GET /storefront/orders`.
   */
  placedBy?: PlacedByDTO
  /**
   * True when {@link OrderDTO.placedBy} is the session Customer. Order-history
   * endpoints only; do not rely on it elsewhere. Sharing is read-only, so an
   * order with `mine === false` must not be resumable, amendable or
   * re-submittable.
   */
  mine?: boolean
  /**
   * The company the order was bought for. Stamped on BOTH payment flows since
   * storefront 1.70.2, so this is **not** an invoice-order marker — discriminate
   * on {@link OrderDTO.paymentType} via {@link isInvoiceBilled} instead.
   */
  subscriberId?: Option<string>
  shippingAddress?: OrderAddressDTO
  shippingCost?: number
  shippingProvider?: ShippingProviderName
  shippingId?: string
  status: OrderStatus
  storeId: string
  shippingInfo?: OrderShippingDTO
  shipments?: ShipmentDTO[]
}

export type CreateUpdateOrderDTO = Omit<
  OrderDTO,
  'items' | 'customer' | 'id' | 'placedBy' | 'mine' | 'subscriberId'
> & {
  items: CreateOrderItemDTO[]
  customer?: Option<CreatCustomerDTO>
  /**
   * The signed-in shopper's identity. Read server-side from the iron-session by
   * the route entry — never from the request body, the same rule the invoice
   * flow follows.
   *
   * When present it *is* the order's customer: the BE resolves it by
   * `(storeId, _id)`, requires the Customer to be `active` (4xx otherwise), and
   * treats `customer.email` / `billingAddress.email` as order data rather than
   * an identity key. When absent on a save of an order that already carries a
   * customer, the existing linkage is preserved — so payment-provider callbacks
   * and the provisioning PUT cannot re-point an order that was linked at
   * creation. Only when both are missing does the BE fall back to resolving by
   * email and auto-creating an INACTIVE Customer (guest checkout).
   */
  customerId?: Option<string>
}

/**
 * Request body for the /storefront/invoice endpoint family.
 *
 * Differs from CreateUpdateOrderDTO:
 * - No `customer` block — SF resolves the shopper via `customerId` and reads
 *   their name / email / addresses from the Customer record.
 * - No `paymentProvider` / `paymentId` — invoice orders carry neither.
 * - The buying company is resolved server-side from `Customer.companyProfile`.
 *   `paymentType` is never supplied by the FE — the BE resolves it from the
 *   company's sales terms to INVOICE or PROFORMA (invoice-billed either way).
 * - No root-level `shippingProvider` / `shippingId` / `shippingInfo` /
 *   `shippingAddress` — the BE rejects them on POST/PUT (per
 *   FEATURE_B2B_ORDER_PLACEMENT.md §7). All shipping intent lives on
 *   `shipments[0]`. The BE reflects that shipment back at the root level on
 *   GET responses (see `OrderDTO`).
 *
 * Item prices, `orderAmount` and `shippingCost` are stored GROSS (VAT-inclusive)
 * — same semantics as UPFRONT orders. `orderAmount` is the order total with
 * `shippingCost` **included**; `shippingCost` is a breakdown of that total, not
 * an addend. `orderTaxAmount` is the VAT portion already inside `orderAmount`,
 * covering goods and freight. B2B's ex-VAT shopper experience is a render-time
 * transformation on the FE, not a storage difference.
 */
export interface CreateInvoiceOrderDTO {
  orderNumber?: string
  storeId: string
  customerId: string
  status: OrderStatus
  currency?: Currency
  orderAmount?: number
  orderTaxAmount?: number
  orderDiscount?: number
  discountCode?: string
  shippingCost?: number
  billingAddress?: OrderAddressDTO
  items: CreateOrderItemDTO[]
  /** Canonical shipment list — exactly 1 element per the B2B spec. The
   *  invoice endpoint reads shipping intent from `shipments[0]` only. */
  shipments?: ShipmentDTO[]
}

/**
 * Body for `PUT /storefront/invoice`. The BE *replaces* fields supplied
 * (including `items` — omit them and the persisted line items are erased),
 * so the FE round-trips the full payload via a `GET` + merge. Distinct
 * from `CreateInvoiceOrderDTO` only so update vs. create call sites stay
 * unambiguous; `orderNumber` is required (it identifies which order).
 */
export type UpdateInvoiceOrderDTO = CreateInvoiceOrderDTO & {
  orderNumber: string
}

export interface OrderAddressDTO {
  address1: string
  address2: string
  attention: string
  country: string
  mobilePhone: string
  postalCode: string
  postalRegion: string
  reference: string
}

export interface CreateServiceItemDTO {
  quantity: number
  serviceId: string
  totalAmount: number
}

export interface OrderItemDTO {
  product: ProductLightDTO
  quantity: number
  unitPrice: number
  originalUnitPrice: number
  vatRate: number
  services?: ServiceItemDTO[] | null
  subItems?: OrderItemDTO[] | null
  additionalInfo?: string
}

export interface ServiceItemDTO {
  service: ServiceDTO
  quantity: number
  totalAmount: number
}

export interface CreateOrderItemDTO {
  productId: ProductLightDTO['id']
  quantity: number
  unitPrice: number
  originalUnitPrice: number
  vatRate: number
  services: CreateServiceItemDTO[] | null
  subItems: CreateOrderItemDTO[]
  additionalInfo?: string
}

export interface UpdateOrderStatusDTO {
  orderStatus: OrderStatus
}

export interface StorefrontOrderLineService {
  quantity: number
  service: ServiceDTO
  totalAmount: number
}

/**
 * A cart line, priced for the buyer who is actually checking out.
 *
 * @remarks
 * **`vatRate` and the prices below are already resolved for the buyer — they
 * are not the catalogue values.** A zero-rated company (intra-EU reverse
 * charge, export) sees `vatRate: 0` with `unitPrice` reduced to the net figure;
 * an EU buyer without a VAT number sees their destination rate with the price
 * re-based onto it. `product.vatRate` and `product.price` still hold the
 * untouched catalogue values, which is what makes the difference easy to miss.
 *
 * Consumers (payment-provider mappers, order builders) must therefore read
 * these fields **verbatim** and never re-apply a VAT conversion — doing so
 * converts twice. Conversely, anything that constructs a line must set the rate
 * and the price *together* via `priceProductForBuyer`; changing one without the
 * other bills a VAT-inclusive figure as net, and nothing downstream can detect
 * it (Order Management explicitly only warns, and only in the other direction).
 *
 * Rows with no rate of their own — service add-ons, fees, shipping — are not
 * covered by this and have to be converted where they are read.
 */
export interface StorefrontOrderLine<P extends ProductLightDTO = ProductLightDTO> {
  product: P
  quantity: number
  /** Buyer-resolved, VAT-inclusive. Pre-discount reference price. */
  originalPrice: number
  /** Buyer-resolved, VAT-inclusive. */
  unitPrice: number
  /** Buyer-resolved percentage (e.g. `25`, or `0` for a zero-rated buyer) —
   *  **not** `product.vatRate`. */
  vatRate: number
  /** Priced from store config at the store's own rate — these are NOT
   *  buyer-resolved, and must be converted by whoever builds the payment row. */
  services: StorefrontOrderLineService[]
  subItems: CreateOrderItemDTO[]
}

export interface StorefrontDiscountCodeResponse {
  data: DiscountDTO
}

export interface StorefrontDiscountCodeListResponse {
  data: DiscountDTO[]
}

export interface UpdateOrderPayload {
  klarnaPayload?: Partial<KlarnaUpdateOrderRequest> & { orderId: string }
  nexiPayload?: NexiUpdateOrderRequest & { paymentId: string }
  sveaPayload?: SveaUpdateOrderModel & { orderId: string | number }
  qliroPayload?: Omit<QliroUpdateOrderRequest, 'MerchantApiKey'> & { orderId: string | number }
  briqpayPayload?: BriqpayUpdateSessionRequest & { sessionId: string }
  // todo: add LEDYER
  ledyerPayload?: any
}

export interface CreateOrderClientPayload {
  locale: 'en' | 'sv'
  companyId: string
  items: CartItem[]
}

export interface CheckoutProduct {
  productId: string
  articleNumber: string
  name?: string
  brand?: string
  quantity: number
  unitPrice: number
  /** @deprecated - isn't used */
  slug?: string
  categoryIds?: string[]
}

export interface CreateOrderRequest {
  storeId: string
  items: StorefrontOrderLine[]
  cartDiscountCode: Option<DiscountDTO>
  cartStateUuid: Option<string>
  /** Country chosen by the shopper at the summary step. Currently only consumed by the Qliro provider. */
  countryCode?: CountryCode
}

/**
 * Slim invoice-order shape returned by `POST /storefront/invoice` and
 * `PUT /storefront/invoice`. The BE strips embedded collections (items,
 * shipments, customer block) on the write endpoints — only the GET path
 * supports `?includeItems=true`. Use this on POST/PUT call sites so the
 * compiler stops you reaching for fields that won't be there.
 */
export type InvoiceOrderSummaryDTO = Pick<
  OrderDTO,
  | 'id'
  | 'orderNumber'
  | 'storeId'
  | 'paymentType'
  | 'status'
  | 'currency'
  | 'orderAmount'
  | 'orderTaxAmount'
  | 'orderDiscount'
  | 'discountCode'
  | 'shippingCost'
  | 'shippingProvider'
  | 'shippingId'
  | 'billingAddress'
  | 'shippingAddress'
  | 'shippingInfo'
  | 'created'
  | 'updated'
>

/**
 * FE → next-api payload for creating an invoice DRAFT order.
 *
 * Distinct from `CreateOrderRequest` because the invoice flow does not need
 * Svea's `cartStateUuid` idempotency tag, and the wire shape between the FE
 * and the route handler should not pretend to be the upfront one. The handler
 * forwards a subset of these to `POST /storefront/invoice` as a
 * `CreateInvoiceOrderDTO` after VAT-stripping.
 */
export interface CreateInvoiceOrderClientRequest {
  storeId: string
  items: StorefrontOrderLine[]
  cartDiscountCode: Option<DiscountDTO>
}

export interface CreateOrderResponse {
  klarnaResponse?: Option<KlarnaOrder>
  ingridResponse?: Option<IngridCreateSessionResponse>
  nshiftResponse?: Option<NshiftCreateSessionResponse>
  nexiResponse?: Option<NexiCreatePaymentResponse>
  sveaResponse?: Option<SveaOrder>
  qliroResponse?: Option<QliroOrder>
  briqpayResponse?: Option<BriqpaySession>
  storefrontOrderResponse: OrderDTO
}

export interface UpdateOrderResponse {
  klarnaResponse?: KlarnaOrder
  nexiResponse?: true
  sveaResponse?: SveaOrder
  qliroResponse?: QliroOrder
  briqpayResponse?: BriqpaySession
}

export interface GetOrderResponse {
  klarnaResponse?: Option<KlarnaOrder>
  nexiResponse?: Option<NexiRetrievePaymentResponse>
  sveaResponse?: Option<SveaOrder>
  qliroResponse?: Option<QliroOrder>
  briqpayResponse?: Option<BriqpaySession>
}
